import { Resource } from '@modelcontextprotocol/sdk/types.js';
import type { Config } from '../types/index.js';
import { findSmartStackStructure, findEntityFiles } from '../lib/detector.js';
import { readText } from '../utils/fs.js';
import path from 'path';

export const entitiesResourceTemplate: Resource = {
  uri: 'smartstack://entities/',
  name: 'SmartStack Entities',
  description: 'Quick reference list of all domain entities with tenant-awareness and table prefixes. Use smartstack://entities/{name} for details.',
  mimeType: 'text/markdown',
};

interface EntitySummary {
  name: string;
  tableName: string;
  tablePrefix: string;
  schema: string;
  isTenantAware: boolean;
  isSystemEntity: boolean;
  baseClass: string;
  hasCode: boolean;
  hasSoftDelete: boolean;
  hasRowVersion: boolean;
  file: string;
  relativePath: string;
}

export async function getEntitiesResource(
  config: Config,
  entityFilter?: string
): Promise<string> {
  const structure = await findSmartStackStructure(config.smartstack.projectPath);

  if (!structure.domain) {
    return '# SmartStack Entities\n\nNo domain project found.';
  }

  const entities: EntitySummary[] = [];

  // Parse entities from domain layer
  const entityFiles = await findEntityFiles(structure.domain);
  for (const file of entityFiles) {
    const entity = await parseEntitySummary(file, structure.root, config);
    if (entity) {
      entities.push(entity);
    }
  }

  // Sort by table prefix, then name
  entities.sort((a, b) => {
    if (a.tablePrefix !== b.tablePrefix) {
      return a.tablePrefix.localeCompare(b.tablePrefix);
    }
    return a.name.localeCompare(b.name);
  });

  // Filter if specified
  const filteredEntities = entityFilter
    ? entities.filter(e =>
        e.name.toLowerCase().includes(entityFilter.toLowerCase()) ||
        e.tableName.toLowerCase().includes(entityFilter.toLowerCase()) ||
        e.tablePrefix.toLowerCase().includes(entityFilter.toLowerCase())
      )
    : entities;

  return formatEntities(filteredEntities, entityFilter, config);
}

async function parseEntitySummary(
  filePath: string,
  rootPath: string,
  config: Config
): Promise<EntitySummary | null> {
  const content = await readText(filePath);

  // Find class declaration with inheritance
  const classMatch = content.match(/public\s+(?:class|record)\s+(\w+)(?:\s*:\s*([^{]+))?/);
  if (!classMatch) return null;

  const entityName = classMatch[1];
  const inheritance = classMatch[2]?.trim() || '';

  // Skip non-entity classes
  if (entityName.endsWith('Dto') || entityName.endsWith('Command') ||
      entityName.endsWith('Query') || entityName.endsWith('Handler') ||
      entityName.endsWith('Configuration') || entityName.endsWith('Service') ||
      entityName.endsWith('Validator') || entityName.endsWith('Exception')) {
    return null;
  }

  // Determine base class
  const baseClassMatch = inheritance.match(/^(\w+)/);
  const baseClass = baseClassMatch ? baseClassMatch[1] : 'Unknown';

  // Check for entity characteristics
  const isSystemEntity = baseClass === 'SystemEntity' ||
                         inheritance.includes('ISystemEntity') ||
                         content.includes('// System entity') ||
                         !content.includes('TenantId');

  const isTenantAware = content.includes('TenantId') &&
                        (content.includes('public Guid TenantId') ||
                         content.includes('public required Guid TenantId'));

  const hasCode = content.includes('public string Code') ||
                  content.includes('public required string Code') ||
                  content.includes('public string? Code');

  const hasSoftDelete = content.includes('IsDeleted') ||
                        inheritance.includes('ISoftDeletable') ||
                        baseClass === 'BaseEntity';

  const hasRowVersion = content.includes('RowVersion') ||
                        inheritance.includes('IHasRowVersion') ||
                        baseClass === 'BaseEntity';

  // Try to determine table name from configuration or conventions
  const { tableName, tablePrefix, schema } = inferTableInfo(entityName, config);

  return {
    name: entityName,
    tableName,
    tablePrefix,
    schema,
    isTenantAware,
    isSystemEntity,
    baseClass,
    hasCode,
    hasSoftDelete,
    hasRowVersion,
    file: filePath,
    relativePath: path.relative(rootPath, filePath),
  };
}

function inferTableInfo(
  entityName: string,
  config: Config
): { tableName: string; tablePrefix: string; schema: string } {
  // Known entity prefixes mapping
  const prefixMapping: Record<string, string> = {
    // Authentication & Authorization
    'User': 'auth_',
    'Role': 'auth_',
    'Permission': 'auth_',
    'UserRole': 'auth_',
    'RolePermission': 'auth_',
    'RefreshToken': 'auth_',

    // Navigation
    'NavigationItem': 'nav_',
    'NavigationMenu': 'nav_',
    'Page': 'nav_',

    // AI
    'Prompt': 'ai_',
    'Completion': 'ai_',
    'Model': 'ai_',
    'Conversation': 'ai_',

    // Configuration
    'Setting': 'cfg_',
    'Configuration': 'cfg_',

    // Workflow
    'Workflow': 'wkf_',
    'WorkflowStep': 'wkf_',
    'WorkflowInstance': 'wkf_',

    // Support
    'Ticket': 'support_',
    'TicketComment': 'support_',

    // Entra (Azure AD)
    'EntraUser': 'entra_',
    'EntraGroup': 'entra_',

    // Licensing
    'License': 'lic_',
    'Subscription': 'lic_',

    // Tenant
    'Tenant': 'tenant_',
    'TenantUser': 'tenant_',
    'TenantUserRole': 'tenant_',
    'TenantInvitation': 'tenant_',
    'Organisation': 'tenant_',

    // Localization
    'Translation': 'loc_',
    'Language': 'loc_',
  };

  // Check if entity name starts with known prefixes
  let tablePrefix = 'ref_'; // Default to reference data

  for (const [pattern, prefix] of Object.entries(prefixMapping)) {
    if (entityName === pattern || entityName.startsWith(pattern)) {
      tablePrefix = prefix;
      break;
    }
  }

  // Check against config table prefixes
  const validPrefixes = config.conventions.tablePrefixes;
  if (!validPrefixes.includes(tablePrefix)) {
    tablePrefix = 'ref_'; // Fallback to ref_ if not in valid list
  }

  // Pluralize entity name for table
  const tableName = `${tablePrefix}${pluralize(entityName)}`;
  const schema = config.conventions.schemas.platform;

  return { tableName, tablePrefix, schema };
}

function pluralize(name: string): string {
  // Simple pluralization rules
  if (name.endsWith('y') && !/[aeiou]y$/i.test(name)) {
    return name.slice(0, -1) + 'ies';
  }
  if (name.endsWith('s') || name.endsWith('x') || name.endsWith('ch') || name.endsWith('sh')) {
    return name + 'es';
  }
  return name + 's';
}

function formatEntities(
  entities: EntitySummary[],
  filter: string | undefined,
  config: Config
): string {
  const lines: string[] = [];

  lines.push('# SmartStack Entities');
  lines.push('');

  if (filter) {
    lines.push(`> Filtered by: \`${filter}\``);
    lines.push('');
  }

  if (entities.length === 0) {
    lines.push('No entities found matching the criteria.');
    return lines.join('\n');
  }

  // Summary stats
  const tenantAwareCount = entities.filter(e => e.isTenantAware).length;
  const systemCount = entities.filter(e => e.isSystemEntity).length;
  const prefixes = [...new Set(entities.map(e => e.tablePrefix))];

  lines.push('## Summary');
  lines.push('');
  lines.push(`- **Total Entities**: ${entities.length}`);
  lines.push(`- **Tenant-Aware**: ${tenantAwareCount}`);
  lines.push(`- **System Entities**: ${systemCount}`);
  lines.push(`- **Table Prefixes Used**: ${prefixes.join(', ')}`);
  lines.push('');

  // Quick reference table
  lines.push('## Quick Reference');
  lines.push('');
  lines.push('| Entity | Table | Tenant | Code | SoftDel | Base |');
  lines.push('|--------|-------|--------|------|---------|------|');

  for (const entity of entities) {
    const tenantIcon = entity.isTenantAware ? '✅' : (entity.isSystemEntity ? '🔒' : '❌');
    const codeIcon = entity.hasCode ? '✅' : '❌';
    const softDelIcon = entity.hasSoftDelete ? '✅' : '❌';

    lines.push(
      `| ${entity.name} | \`${entity.tableName}\` | ${tenantIcon} | ${codeIcon} | ${softDelIcon} | ${entity.baseClass} |`
    );
  }
  lines.push('');

  // Group by prefix
  lines.push('## By Domain');
  lines.push('');

  const byPrefix = new Map<string, EntitySummary[]>();
  for (const entity of entities) {
    const list = byPrefix.get(entity.tablePrefix) || [];
    list.push(entity);
    byPrefix.set(entity.tablePrefix, list);
  }

  const prefixNames: Record<string, string> = {
    'auth_': 'Authentication & Authorization',
    'nav_': 'Navigation',
    'ai_': 'AI & Prompts',
    'cfg_': 'Configuration',
    'wkf_': 'Workflows',
    'support_': 'Support',
    'entra_': 'Microsoft Entra',
    'lic_': 'Licensing',
    'tenant_': 'Multi-Tenant',
    'loc_': 'Localization',
    'ref_': 'Reference Data',
    'usr_': 'User Data',
  };

  for (const [prefix, prefixEntities] of byPrefix) {
    const domainName = prefixNames[prefix] || prefix;
    lines.push(`### ${domainName} (\`${prefix}\`)`);
    lines.push('');

    for (const entity of prefixEntities) {
      const badges: string[] = [];
      if (entity.isTenantAware) badges.push('tenant-aware');
      if (entity.isSystemEntity) badges.push('system');
      if (entity.hasCode) badges.push('has-code');

      lines.push(`- **${entity.name}** → \`${entity.tableName}\``);
      if (badges.length > 0) {
        lines.push(`  - ${badges.map(b => `\`${b}\``).join(' ')}`);
      }
      lines.push(`  - File: \`${entity.relativePath}\``);
    }
    lines.push('');
  }

  // Conventions reminder
  lines.push('## Conventions');
  lines.push('');
  lines.push('| Property | Convention |');
  lines.push('|----------|------------|');
  lines.push(`| Schema | \`${config.conventions.schemas.platform}\` |`);
  lines.push(`| Valid Prefixes | ${config.conventions.tablePrefixes.map(p => `\`${p}\``).join(', ')} |`);
  lines.push('| Tenant-aware | Must have `TenantId` (GUID, NOT NULL) |');
  lines.push('| System entity | No `TenantId`, used for platform-level data |');
  lines.push('| Code field | Lowercase, unique per tenant (or globally for system) |');
  lines.push('');

  // Legend
  lines.push('## Legend');
  lines.push('');
  lines.push('- ✅ = Feature present');
  lines.push('- ❌ = Feature absent');
  lines.push('- 🔒 = System entity (no tenant scope)');
  lines.push('');

  return lines.join('\n');
}
